import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { Calendar, MapPin, ExternalLink, ArrowRight, ShoppingBag, Instagram } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import RebelTeamSection from "@/components/RebelTeamSection";
import EmbedErrorBoundary from "@/components/EmbedErrorBoundary";
import AddToCartModal from "@/components/AddToCartModal";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { resolveProductOptions } from "@/lib/productOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function HeroSection({ content }) {
  const { t, lang } = useLanguage();
  const heroTitle = content?.hero_title || "REBELKY";
  const tagline = (lang === "cz" ? content?.hero_tagline_cz : content?.hero_tagline_en) || t.hero.tagline;
  const bgImage = content?.hero_bg_image
    ? (content.hero_bg_image.startsWith("http") ? content.hero_bg_image : `${BACKEND}${content.hero_bg_image}`)
    : "https://images.unsplash.com/photo-1768054180300-ab772bd09d0e?crop=entropy&cs=srgb&fm=jpg&w=1920&q=60";
  return (
    <section
      data-testid="hero-section"
      className="relative flex flex-col items-center justify-center overflow-hidden"
      style={{ minHeight: "100vh", background: "#050505" }}
    >
      {/* Animated blobs */}
      <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        <div className="hero-blob" style={{ width: 500, height: 500, background: "radial-gradient(circle, rgba(255,0,90,0.18) 0%, transparent 70%)", top: "-100px", left: "-100px", animation: "blob-float 8s ease-in-out infinite" }} />
        <div className="hero-blob" style={{ width: 400, height: 400, background: "radial-gradient(circle, rgba(0,229,255,0.1) 0%, transparent 70%)", bottom: "0", right: "-50px", animation: "blob-float-2 10s ease-in-out infinite" }} />
        <div className="hero-blob" style={{ width: 300, height: 300, background: "radial-gradient(circle, rgba(255,0,90,0.08) 0%, transparent 70%)", top: "40%", left: "50%", animation: "blob-float 12s ease-in-out infinite 2s" }} />
      </div>

      {/* Background image overlay */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.08 }} />

      {/* Grid lines */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)", backgroundSize: "80px 80px", pointerEvents: "none" }} />

      {/* Content */}
      <div className="relative z-10 text-center px-6" style={{ maxWidth: "900px" }}>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="section-label mb-6"
        >
          ★ Dívčí kapela ★ Girl Band ★
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          data-testid="hero-title"
          style={{
            fontFamily: "Unbounded, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(4rem, 16vw, 12rem)",
            letterSpacing: "-0.04em",
            textTransform: "uppercase",
            lineHeight: 0.9,
            color: "#FFFFFF",
            marginBottom: "1.5rem",
            display: "inline-flex",
          }}
        >
          {Array.from(heroTitle).map((letter, i) => (
            <motion.span
              key={`${i}-${letter}`}
              className="hero-letter"
              style={{ display: "inline-block", animationDelay: `${i * 0.18}s` }}
            >
              {letter}
            </motion.span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{ color: "#A1A1AA", fontSize: "1.1rem", marginBottom: "2.5rem", fontFamily: "Manrope, sans-serif" }}
        >
          {tagline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <a
            href="https://www.youtube.com/@kapela_rebelky"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-follow"
            className="btn-rebel rounded-none inline-flex items-center gap-2 justify-center"
          >
            {t.hero.followUs}
          </a>
          <Link
            to="/koncerty"
            data-testid="hero-cta-concerts"
            className="btn-outline-rebel rounded-none inline-flex items-center gap-2 justify-center"
          >
            {t.hero.upcomingShows} <ArrowRight size={14} />
          </Link>
        </motion.div>
      </div>

      {/* Bottom Marquee */}
      <div className="absolute bottom-0 left-0 right-0" style={{ borderTop: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,0,90,0.06)", padding: "0.75rem 0" }}>
        <Marquee speed={40} gradient={false}>
          {["REBELKY", "SOFI", "AGI", "LEYLA", "NATY", "REBEL GIRLS", "REBELTEAM", "ZPÍVÁME", "TANCUJEME", "REBELUJEME"].map((w, i) => (
            <span key={i} className="marquee-text mx-8" style={{ color: i % 2 === 0 ? "#FF005A" : "#FFFFFF" }}>
              {w} ★
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}

function SocialSection({ settings, content }) {
  const { t, lang } = useLanguage();
  const [autoYtId, setAutoYtId] = useState("");
  const igThumbs = (content?.instagram_thumbs || []).filter(Boolean);
  const ttThumbs = (content?.tiktok_thumbs || []).filter(Boolean);
  const igPosts = (settings?.instagram_posts || []).filter(Boolean);
  const ttPosts = (settings?.tiktok_posts || []).filter(Boolean);
  const resolveThumb = (u) => u?.startsWith("http") ? u : `${BACKEND}${u}`;
  const tiktokUser = settings?.tiktok_username || "rebelky_official";
  const igUrl = settings?.instagram_url || "https://www.instagram.com/rebelky_official/";
  const ytChannel = settings?.youtube_channel_id || "UCGwtynqWloUgIxgfDaNN6Pg";

  // Auto-fetch latest YouTube video via RSS
  useEffect(() => {
    if (settings?.youtube_latest_video_id) return; // admin-pinned overrides
    if (!ytChannel) return;
    axios.get(`${BACKEND}/api/social/youtube-latest`, { params: { channel_id: ytChannel } })
      .then(r => { if (r.data?.video_id) setAutoYtId(r.data.video_id); })
      .catch(() => {});
  }, [ytChannel, settings?.youtube_latest_video_id]);

  const ytVideoId = settings?.youtube_latest_video_id || autoYtId || "8r2pYx7O8Dk";

  // Unified card height for all three platforms
  const CARD_BODY_HEIGHT = 280;

  const cardShell = {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  };
  const cardHeader = (color, label) => (
    <div className="p-4 flex items-center gap-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <div className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", letterSpacing: "0.2em", color: "#A1A1AA", textTransform: "uppercase" }}>{label}</span>
    </div>
  );

  const thumbsGrid = (thumbs, href, testIdPrefix) => (
    <div style={{ height: CARD_BODY_HEIGHT, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px", padding: "2px" }} data-testid={`${testIdPrefix}-thumbs`}>
      {thumbs.slice(0, 4).map((src, i) => (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testIdPrefix}-thumb-${i}`}
          style={{ overflow: "hidden", background: "#050505", position: "relative" }}
        >
          <img src={resolveThumb(src)} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s" }} onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.08)"} onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"} />
        </a>
      ))}
    </div>
  );

  const followBtnBase = {
    fontFamily: "Unbounded, sans-serif",
    fontSize: "0.6rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    padding: "0.65rem",
    textDecoration: "none",
    display: "block",
    textAlign: "center",
    width: "100%",
    cursor: "pointer",
    border: "none",
  };

  return (
    <section data-testid="social-section" style={{ background: "#050505", padding: "5rem 0" }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-10">
          <p className="section-label mb-2">{t.social.title.toUpperCase()}</p>
          <h2 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "clamp(1.5rem, 4vw, 2.5rem)", color: "#FFFFFF" }}>
            {lang === "cz" ? "SLEDUJ NÁS" : "FOLLOW US"}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" style={{ alignItems: "stretch" }}>
          {/* YouTube */}
          <div data-testid="youtube-embed" className="glass-card rounded-2xl overflow-hidden" style={cardShell}>
            {cardHeader("#FF0000", "YouTube")}
            <div style={{ height: CARD_BODY_HEIGHT, position: "relative", background: "#000" }}>
              <EmbedErrorBoundary label="YouTube" fallback={
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#A1A1AA" }}>Video se nepodařilo načíst</div>
              }>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${ytVideoId}?rel=0&modestbranding=1`}
                  title="YouTube"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </EmbedErrorBoundary>
            </div>
            <div className="p-3" style={{ borderTop: "1px solid rgba(255,255,255,0.06)", marginTop: "auto" }}>
              <a href="https://www.youtube.com/@kapela_rebelky" target="_blank" rel="noopener noreferrer" data-testid="youtube-channel-link"
                style={{ ...followBtnBase, background: "#FF0000", color: "#FFFFFF" }}>
                {t.social.youtube}
              </a>
            </div>
          </div>

          {/* TikTok */}
          <div data-testid="tiktok-card" className="glass-card rounded-2xl overflow-hidden" style={cardShell}>
            {cardHeader("#FF0050", "TikTok")}
            {ttThumbs.length > 0 ? (
              thumbsGrid(ttThumbs, `https://www.tiktok.com/@${tiktokUser}`, "tiktok")
            ) : (
              <div style={{ height: CARD_BODY_HEIGHT, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "1.5rem", textAlign: "center" }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="#FF0050" style={{ marginBottom: "1rem" }}>
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.19 8.19 0 004.79 1.54V6.78a4.85 4.85 0 01-1.02-.09z" />
                </svg>
                <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.9rem", color: "#FFFFFF", marginBottom: "0.3rem" }}>@{tiktokUser}</p>
                <p style={{ color: "#A1A1AA", fontSize: "0.8rem" }}>{t.social.tiktok}</p>
              </div>
            )}
            <div className="p-3" style={{ borderTop: "1px solid rgba(255,255,255,0.06)", marginTop: "auto" }}>
              <a href={`https://www.tiktok.com/@${tiktokUser}`} target="_blank" rel="noopener noreferrer" data-testid="tiktok-follow-btn"
                style={{ ...followBtnBase, background: "#FF0050", color: "#FFFFFF" }}>
                {t.social.followTiktok}
              </a>
            </div>
          </div>

          {/* Instagram */}
          <div data-testid="instagram-card" className="glass-card rounded-2xl overflow-hidden" style={cardShell}>
            {cardHeader("linear-gradient(135deg, #f09433, #dc2743, #bc1888)", "Instagram")}
            {igThumbs.length > 0 ? (
              thumbsGrid(igThumbs, igUrl, "instagram")
            ) : (
              <div style={{ height: CARD_BODY_HEIGHT, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "1.5rem", textAlign: "center" }}>
                <div style={{
                  width: "56px", height: "56px",
                  background: "linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                  borderRadius: "14px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: "1rem",
                }}>
                  <Instagram size={28} style={{ color: "white" }} />
                </div>
                <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.9rem", color: "#FFFFFF", marginBottom: "0.3rem" }}>@rebelky_official</p>
                <p style={{ color: "#A1A1AA", fontSize: "0.8rem" }}>
                  {lang === "cz" ? "Sleduj nás na Instagramu" : "Follow us on Instagram"}
                </p>
              </div>
            )}
            <div className="p-3" style={{ borderTop: "1px solid rgba(255,255,255,0.06)", marginTop: "auto" }}>
              <a href={igUrl} target="_blank" rel="noopener noreferrer" data-testid="instagram-follow-btn"
                style={{ ...followBtnBase, background: "linear-gradient(135deg, #f09433, #e6683c, #dc2743)", color: "#FFFFFF" }}>
                {lang === "cz" ? "Sledovat na Instagramu" : "Follow on Instagram"}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function ConcertsPreview({ events }) {
  const { t, lang } = useLanguage();

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === "cz" ? "cs-CZ" : "en-GB", { day: "numeric", month: "long", year: "numeric" });
  };

  return (
    <section data-testid="concerts-preview" style={{ background: "#0A0A0A", padding: "5rem 0" }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="section-label mb-2">{t.concerts.subtitle}</p>
            <h2 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "clamp(1.5rem, 4vw, 2.5rem)", color: "#FFFFFF" }}>
              {t.concerts.title.toUpperCase()}
            </h2>
          </div>
          <Link to="/koncerty" data-testid="view-all-concerts" className="btn-outline-rebel rounded-none" style={{ fontSize: "0.65rem", padding: "0.6rem 1.5rem" }}>
            {t.concerts.viewAll} <ArrowRight size={12} className="inline ml-1" />
          </Link>
        </div>

        {events.length === 0 ? (
          <p style={{ color: "#52525B" }}>{t.concerts.noEvents}</p>
        ) : (
          <div className="flex flex-col gap-2">
            {events.slice(0, 4).map((event, i) => (
              <motion.div
                key={event._id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                data-testid={`event-row-${i}`}
                className="glass-card"
                style={{ padding: "1.2rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", borderRadius: "8px", transition: "all 0.2s ease" }}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div style={{ textAlign: "center", minWidth: "60px" }}>
                    <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "1.4rem", color: "#FF005A", lineHeight: 1 }}>
                      {new Date(event.date).getDate().toString().padStart(2, "0")}
                    </p>
                    <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.55rem", color: "#A1A1AA", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                      {new Date(event.date).toLocaleDateString(lang === "cz" ? "cs-CZ" : "en-GB", { month: "short" })}
                    </p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.9rem", color: "#FFFFFF", textTransform: "uppercase" }}>
                      {event.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <MapPin size={12} style={{ color: "#FF005A", flexShrink: 0 }} />
                      <p style={{ color: "#A1A1AA", fontSize: "0.8rem" }}>{event.location}</p>
                      {event.time && <span style={{ color: "#52525B", fontSize: "0.8rem" }}>· {event.time}</span>}
                    </div>
                  </div>
                </div>
                {event.ticket_url ? (
                  <a
                    href={event.ticket_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`buy-tickets-${i}`}
                    className="btn-rebel rounded-none"
                    style={{ fontSize: "0.6rem", padding: "0.5rem 1rem", whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    {t.concerts.buyTickets}
                  </a>
                ) : (
                  <span style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", color: "#00E5FF", letterSpacing: "0.1em", border: "1px solid rgba(0,229,255,0.3)", padding: "0.5rem 1rem" }}>
                    {t.concerts.freeEntry}
                  </span>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function SpotifyBanner({ settings }) {
  const { t, lang } = useLanguage();
  const artistId = settings?.spotify_artist_id || "1igMI4JGH7TXcozyMzP48M";
  return (
    <section
      data-testid="spotify-banner"
      style={{
        background: "linear-gradient(135deg, #050505 0%, #0a1a0e 50%, #050505 100%)",
        padding: "5rem 0",
        position: "relative",
        overflow: "hidden",
        borderTop: "1px solid rgba(29, 185, 84, 0.12)",
        borderBottom: "1px solid rgba(29, 185, 84, 0.12)",
      }}
    >
      {/* Spotify-green glow accents */}
      <div style={{ position: "absolute", top: "-50%", right: "-10%", width: "600px", height: "600px", background: "radial-gradient(circle, rgba(29,185,84,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-40%", left: "-5%", width: "400px", height: "400px", background: "radial-gradient(circle, rgba(29,185,84,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          {/* Left: text + CTA */}
          <div>
            <p className="section-label mb-3" style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#1DB954", boxShadow: "0 0 10px #1DB954" }} />
              SPOTIFY
            </p>
            <h2
              data-testid="spotify-heading"
              style={{
                fontFamily: "Unbounded, sans-serif",
                fontWeight: 900,
                fontSize: "clamp(1.75rem, 4.5vw, 3rem)",
                color: "#FFFFFF",
                textTransform: "uppercase",
                letterSpacing: "-0.03em",
                lineHeight: 1.05,
                marginBottom: "1rem",
              }}
            >
              {lang === "cz" ? (
                <>POSLOUCHEJ<br />
                  <span style={{ color: "#1DB954" }}>REBELKY</span> NA SPOTIFY
                </>
              ) : (
                <>LISTEN TO<br />
                  <span style={{ color: "#1DB954" }}>REBELKY</span> ON SPOTIFY
                </>
              )}
            </h2>
            <p style={{ color: "#A1A1AA", fontSize: "0.95rem", lineHeight: 1.6, fontFamily: "Manrope, sans-serif", marginBottom: "1.8rem", maxWidth: "420px" }}>
              {lang === "cz"
                ? "Celý katalog skladeb, playlisty i poslední singly. Přidej si nás do knihovny a nezmeškej žádnou novou písničku."
                : "Full catalogue, playlists and the latest singles. Add us to your library and never miss a new track."}
            </p>
            <a
              href={`https://open.spotify.com/artist/${artistId}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="spotify-link"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.6rem",
                background: "#1DB954",
                color: "#050505",
                fontFamily: "Unbounded, sans-serif",
                fontWeight: 800,
                fontSize: "0.7rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                padding: "0.9rem 1.8rem",
                textDecoration: "none",
                borderRadius: "999px",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                boxShadow: "0 6px 24px rgba(29,185,84,0.35)",
              }}
              onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 32px rgba(29,185,84,0.55)"; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(29,185,84,0.35)"; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z" />
              </svg>
              {lang === "cz" ? "Otevřít na Spotify" : "Open on Spotify"}
            </a>
          </div>

          {/* Right: embedded player */}
          <EmbedErrorBoundary
            label="Spotify"
            fallback={
              <div
                style={{
                  background: "#0A0A0A", borderRadius: "14px", padding: "2rem",
                  border: "1px solid rgba(29,185,84,0.18)", textAlign: "center", minHeight: 352,
                  display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "0.7rem",
                }}
              >
                <p style={{ color: "#1DB954", fontFamily: "Unbounded, sans-serif", fontSize: "0.7rem", letterSpacing: "0.2em" }}>SPOTIFY</p>
                <p style={{ color: "#A1A1AA", fontSize: "0.85rem", fontFamily: "Manrope, sans-serif" }}>
                  Přehrávač se nepodařilo načíst.
                </p>
                <a href={`https://open.spotify.com/artist/${artistId}`} target="_blank" rel="noreferrer" style={{ color: "#1DB954", fontFamily: "Unbounded, sans-serif", fontSize: "0.65rem", letterSpacing: "0.15em", textDecoration: "underline" }}>
                  OTEVŘÍT NA SPOTIFY →
                </a>
              </div>
            }
          >
            <div
              data-testid="spotify-embed"
              style={{
                background: "#0A0A0A",
                borderRadius: "14px",
                padding: "0.5rem",
                border: "1px solid rgba(29,185,84,0.18)",
                boxShadow: "0 12px 40px rgba(29,185,84,0.12)",
              }}
            >
              <iframe
                style={{ display: "block", borderRadius: "10px" }}
                src={`https://open.spotify.com/embed/artist/${artistId}?utm_source=generator&theme=0`}
                width="100%"
                height="352"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title="Spotify – Rebelky"
              />
            </div>
          </EmbedErrorBoundary>
        </div>
      </div>
    </section>
  );
}

function MerchPreview({ products }) {
  const { t, lang } = useLanguage();
  const { addItem } = useCart();
  const [modalProduct, setModalProduct] = useState(null);

  const needsModal = (p) => {
    const o = resolveProductOptions(p);
    return o.showSizes || o.showColors || o.showCustom;
  };

  const handle = (product) => {
    if (needsModal(product)) { setModalProduct(product); return; }
    addItem(product);
  };

  return (
    <section data-testid="merch-preview" style={{ background: "#050505", padding: "5rem 0" }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="section-label mb-2">{t.merch.subtitle}</p>
            <h2 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "clamp(1.5rem, 4vw, 2.5rem)", color: "#FFFFFF" }}>
              {t.merch.title.toUpperCase()}
            </h2>
          </div>
          <Link to="/merch" data-testid="view-all-merch" className="btn-outline-rebel rounded-none" style={{ fontSize: "0.65rem", padding: "0.6rem 1.5rem" }}>
            {t.merch.viewAll} <ArrowRight size={12} className="inline ml-1" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {products.slice(0, 4).map((product, i) => (
            <motion.div
              key={product._id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              data-testid={`product-card-${i}`}
              className="product-card card-hover"
              style={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", overflow: "hidden", cursor: "pointer" }}
            >
              <Link to={`/merch/${product._id}`}>
                <div style={{ aspectRatio: "1", overflow: "hidden", background: "#121212" }}>
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="product-image w-full h-full"
                    onError={e => { e.target.src = "https://images.unsplash.com/photo-1603208636533-5b965ef2dc22?w=400&q=80"; }}
                  />
                </div>
              </Link>
              <div style={{ padding: "1rem" }}>
                <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.7rem", fontWeight: 700, color: "#FFFFFF", textTransform: "uppercase", marginBottom: "0.4rem" }} className="line-clamp-2">
                  {product.name}
                </p>
                <p style={{ color: "#FF005A", fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "1rem", marginBottom: "0.8rem" }}>
                  {product.price} Kč
                </p>
                <button
                  onClick={() => handle(product)}
                  data-testid={`add-to-cart-${i}`}
                  className="btn-rebel rounded-none w-full"
                  style={{ fontSize: "0.55rem", padding: "0.5rem" }}
                >
                  <ShoppingBag size={10} className="inline mr-1" /> {needsModal(product) ? (lang === "cz" ? "VYBRAT VELIKOST" : "PICK SIZE") : t.merch.addToCart}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      <AddToCartModal
        product={modalProduct}
        open={!!modalProduct}
        onClose={() => setModalProduct(null)}
        onConfirm={({ size, color, custom_options, quantity }) => addItem(modalProduct, quantity || 1, size, color, custom_options)}
      />
    </section>
  );
}

function AboutPreview({ content }) {
  const { t, lang } = useLanguage();
  const fallback = [
    { name: "Sofi", color: "#FF005A", desc_cz: "Malá ale šikovná. Crazy a talentovaná.", desc_en: "Small but mighty. Crazy and talented.", photo_url: "" },
    { name: "Agi", color: "#00E5FF", desc_cz: "Blondýna s talentem. Naplno a bez kompromisů.", desc_en: "Blonde with talent. Fully committed, no compromises.", photo_url: "" },
    { name: "Leyla", color: "#E5FF00", desc_cz: "Napůl Egypťanka. Tancuje od narození.", desc_en: "Half Egyptian. Dancing since birth.", photo_url: "" },
    { name: "Naty", color: "#FF005A", desc_cz: "Bláznivé nápady, které vždy fungují.", desc_en: "Crazy ideas that always work.", photo_url: "" },
  ];
  const members = (content?.about_members && content.about_members.length > 0) ? content.about_members : fallback;
  const resolveImg = (u) => u?.startsWith("http") ? u : (u ? `${BACKEND}${u}` : "");

  return (
    <section data-testid="about-preview" style={{ background: "#0A0A0A", padding: "5rem 0" }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="section-label mb-2">{t.about.members}</p>
            <h2 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "clamp(1.5rem, 4vw, 2.5rem)", color: "#FFFFFF" }}>
              {t.about.title.toUpperCase()}
            </h2>
          </div>
          <Link to="/o-nas" data-testid="view-about" className="btn-outline-rebel rounded-none" style={{ fontSize: "0.65rem", padding: "0.6rem 1.5rem" }}>
            {t.about.learnMore} <ArrowRight size={12} className="inline ml-1" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {members.map((member, i) => {
            const photoUrl = resolveImg(member.photo_url);
            return (
              <motion.div
                key={`${member.name}-${i}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                data-testid={`member-card-${member.name.toLowerCase()}`}
                className="glass-card card-hover rounded-2xl p-6 text-center"
              >
                {photoUrl ? (
                  <div
                    className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden"
                    style={{ border: `2px solid ${member.color}60`, boxShadow: `0 0 20px ${member.color}30` }}
                  >
                    <img src={photoUrl} alt={member.name} loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `${member.color}20`, border: `2px solid ${member.color}40` }}>
                    <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "1.2rem", color: member.color }}>
                      {member.name[0]}
                    </span>
                  </div>
                )}
                <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "1rem", color: "#FFFFFF", textTransform: "uppercase", marginBottom: "0.5rem" }}>
                  {member.name}
                </p>
                <p style={{ color: "#A1A1AA", fontSize: "0.8rem", lineHeight: 1.5 }}>
                  {lang === "cz" ? member.desc_cz : member.desc_en}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [content, setContent] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/events`),
      axios.get(`${API}/products?featured_only=true`),
      axios.get(`${API}/settings`),
      axios.get(`${API}/content`),
    ]).then(([evRes, prRes, stRes, coRes]) => {
      setEvents(evRes.data);
      setProducts(prRes.data);
      setSettings(stRes.data);
      setContent(coRes.data);
    }).catch(console.error);
  }, []);

  return (
    <div style={{ background: "#050505" }}>
      <SEO
        title="Domů – Sofi, Agi, Leyla & Naty"
        description="Rebelky – česká dívčí kapela. Sofi, Agi, Leyla a Naty. Pop hudba, koncerty Bezvafest tour 2026, oficiální merch e-shop, novinky a Spotify. Booking & produkce: Pinkofein s.r.o."
        url="/"
      />
      <Navbar />
      <HeroSection content={content} />
      <SocialSection settings={settings} content={content} />
      <ConcertsPreview events={events} />
      <SpotifyBanner settings={settings} />
      <MerchPreview products={products} />
      <AboutPreview content={content} />
      <RebelTeamSection content={content} />
      <Footer content={content} />
    </div>
  );
}
